/* Keep Shopify's native search form and Buddy's account/cart/menu handlers. */
if (!customElements.get('wellix-header-search')) {
  customElements.define('wellix-header-search', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.toggle = this.querySelector('.wl-search__toggle');
      this.dialog = this.querySelector('dialog');
      if (!this.toggle || !this.dialog || !this.dialog.showModal) return;
      this.toggle.setAttribute('role', 'button');
      this.toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      }, options);
      this.toggle.addEventListener('keydown', (event) => {
        if (event.key === ' ') {
          event.preventDefault();
          this.open();
        }
      }, options);
      this.querySelector('.wl-search__close').addEventListener('click', () => this.dialog.close(), options);
      // Only a gesture starting and ending on the backdrop closes the drawer.
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropPress = this.isBackdrop(event);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.backdropPress && this.isBackdrop(event)) this.dialog.close();
        this.backdropPress = false;
      }, options);
      this.dialog.addEventListener('close', () => this.onClose(), options);
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX >= rect.right || event.clientY < rect.top || event.clientY >= rect.bottom;
    }

    open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('wl-search-open');
      this.querySelector('input[type="search"]').focus({ preventScroll: true });
    }

    onClose() {
      this.toggle.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('.wl-search__dialog[open]')) document.body.classList.remove('wl-search-open');
      this.querySelector('predictive-search')?.close();
      if (this.isConnected) this.toggle.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      if (!document.querySelector('.wl-search__dialog[open]')) document.body.classList.remove('wl-search-open');
    }
  });
}

(() => {
  const headers = new Map();
  function setup(root) {
    root.querySelectorAll('.wl-header').forEach((header) => {
      if (headers.has(header)) return;
      const menu = header.querySelector(':scope > .header__inline-menu');
      const list = menu?.querySelector('.wbsimplemenuxs > .list-menu--inline');
      const logo = header.querySelector(':scope > .header__heading, :scope > .header__heading-link');
      const icons = header.querySelector(':scope > .header__icons');
      const wrapper = header.closest('.wl-header-overlay');
      const section = header.closest('.section-header');
      let scrollFrame;
      const updateSticky = () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          if (!wrapper || !section) return;
          wrapper.classList.toggle('wl-header-is-stuck', window.innerWidth >= 992 && window.scrollY > 0 && section.getBoundingClientRect().top <= 0);
        });
      };
      window.addEventListener('scroll', updateSticky, { passive: true });
      window.addEventListener('resize', updateSticky);
      updateSticky();
      if (!list || !logo || !icons) {
        headers.set(header, () => {
          cancelAnimationFrame(scrollFrame);
          window.removeEventListener('scroll', updateSticky);
          window.removeEventListener('resize', updateSticky);
        });
        return;
      }
      let frame;
      const measure = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = header.clientWidth;
          header.closest('.wl-header-wrapper')?.style.setProperty('--wl-header-height', `${header.getBoundingClientRect().height}px`);
          if (!width || window.innerWidth < 992) return;
          const side = Math.max(logo.getBoundingClientRect().width, icons.getBoundingClientRect().width);
          const gap = parseFloat(getComputedStyle(header).columnGap) || 24;
          const compact = list.getBoundingClientRect().width > width - 2 * side - Math.max(48, 2 * gap);
          if (!compact && header.classList.contains('wl-header--compact')) {
            const drawer = header.querySelector('header-drawer.mobile-drawer');
            if (drawer?.querySelector('details[open]')) drawer.closeMenuDrawer(new Event('resize'), drawer.querySelector('summary'));
          }
          header.classList.toggle('wl-header--compact', compact);
          menu.inert = compact;
          if (compact) menu.setAttribute('aria-hidden', 'true');
          else menu.removeAttribute('aria-hidden');
        });
      };
      const observer = new ResizeObserver(measure);
      [header, list, logo, icons].forEach((element) => observer.observe(element));
      window.addEventListener('resize', measure);
      headers.set(header, () => {
        observer.disconnect();
        cancelAnimationFrame(frame);
        window.removeEventListener('resize', measure);
        cancelAnimationFrame(scrollFrame);
        window.removeEventListener('scroll', updateSticky);
        window.removeEventListener('resize', updateSticky);
      });
      document.fonts?.ready.then(() => { if (header.isConnected) measure(); });
      measure();
    });
  }
  setup(document);
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [header, cleanup] of headers) {
      if (event.target.contains(header)) {
        cleanup();
        headers.delete(header);
      }
    }
  });
})();
